import { useEffect, useState, useRef, useCallback } from 'react';

export const useRealTimeTracking = (deliveryId) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [routeHistory, setRouteHistory] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(0); // 0: disconnected, 1: connected, -1: error
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const getWebSocketUrl = useCallback(() => {
    // Use the backend URL from environment, converting http to ws
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    
    if (backendUrl.includes('localhost') || backendUrl.includes('127.0.0.1')) {
      // Local development
      return `${wsProtocol}//${window.location.hostname}:8001/ws/tracking/${deliveryId}`;
    }
    
    // Production: Use the same host as backend URL but with ws protocol
    try {
      const url = new URL(backendUrl);
      return `${wsProtocol}//${url.host}/ws/tracking/${deliveryId}`;
    } catch {
      return `${wsProtocol}//${window.location.host}/ws/tracking/${deliveryId}`;
    }
  }, [deliveryId]);

  useEffect(() => {
    if (!deliveryId) return;

    const wsUrl = getWebSocketUrl();
    console.log('Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected for delivery:', deliveryId);
      setConnectionStatus(1);
      reconnectAttemptsRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        // Handle different message types
        if (message.type === 'location_update' && message.data) {
          const update = message.data;
          if (update.latitude && update.longitude) {
            setCurrentLocation(update);
            setRouteHistory(prev => [...prev, update]);
          }
        } else if (message.latitude && message.longitude) {
          // Direct location data
          setCurrentLocation(message);
          setRouteHistory(prev => [...prev, message]);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setConnectionStatus(-1);
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setConnectionStatus(0);
      socketRef.current = null;
      
      // Auto-reconnect with exponential backoff
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && deliveryId) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          // Will reconnect via useEffect dependency change
        }, delay);
      }
    };

    socketRef.current = ws;

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [deliveryId, getWebSocketUrl]);

  // Function to send location updates (for riders)
  const sendLocation = useCallback((location) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'location_update',
        latitude: location.latitude,
        longitude: location.longitude,
        speed: location.speed,
        heading: location.heading,
        timestamp: new Date().toISOString()
      }));
    }
  }, []);

  return {
    currentLocation,
    routeHistory,
    connectionStatus,
    sendLocation,
    isConnected: connectionStatus === 1
  };
};
